import { useState, useRef, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { 
  CreditCard, Download, Printer, Mail, RefreshCw, 
  User, Phone, Globe, Palette, Check, Copy,
  Instagram, Facebook, Linkedin, Upload, Image, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import html2canvas from 'html2canvas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = window.location.origin;

// Plantillas de diseño
const CARD_TEMPLATES = [
  {
    id: 'classic',
    name: 'Clásico',
    bgColor: 'bg-white',
    textColor: 'text-gray-900',
    accentColor: 'bg-violet-600',
    borderColor: 'border-gray-200'
  },
  {
    id: 'dark',
    name: 'Elegante Oscuro',
    bgColor: 'bg-gray-900',
    textColor: 'text-white',
    accentColor: 'bg-violet-500',
    borderColor: 'border-gray-700'
  },
  {
    id: 'gradient',
    name: 'Gradiente',
    bgColor: 'bg-gradient-to-br from-violet-600 to-purple-700',
    textColor: 'text-white',
    accentColor: 'bg-white',
    borderColor: 'border-transparent'
  },
  {
    id: 'minimal',
    name: 'Minimalista',
    bgColor: 'bg-gray-50',
    textColor: 'text-gray-800',
    accentColor: 'bg-gray-900',
    borderColor: 'border-gray-100'
  }
];

const BusinessCardGenerator = ({ user, token }) => {
  const [selectedTemplate, setSelectedTemplate] = useState('classic');
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [customImage, setCustomImage] = useState(null);
  const [customImagePreview, setCustomImagePreview] = useState(null);
  const cardRef = useRef(null);
  const cardBackRef = useRef(null);
  const fileInputRef = useRef(null);

  const profileUrl = `${SITE_URL}/${user?.username}`;

  useEffect(() => {
    fetchProfileData();
  }, [token]);

  const fetchProfileData = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${BACKEND_URL}/api/user/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfileData(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const currentTemplate = CARD_TEMPLATES.find(t => t.id === selectedTemplate) || CARD_TEMPLATES[0];

  // Manejar subida de imagen personalizada
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('La imagen debe ser menor a 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setCustomImage(file);
        setCustomImagePreview(event.target.result);
        toast.success('Imagen cargada correctamente');
      };
      reader.readAsDataURL(file);
    }
  };

  const removeCustomImage = () => {
    setCustomImage(null);
    setCustomImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadCard = async (side = 'front') => {
    const element = side === 'front' ? cardRef.current : cardBackRef.current;
    if (!element) return;

    setDownloading(true);
    try {
      const canvas = await html2canvas(element, {
        scale: 4, // Mayor resolución
        backgroundColor: null,
        useCORS: true,
        logging: false,
        allowTaint: true,
        width: element.offsetWidth,
        height: element.offsetHeight
      });

      const link = document.createElement('a');
      link.download = `tarjeta-${user?.username}-${side}-${selectedTemplate}.png`;
      link.href = canvas.toDataURL('image/png', 1.0);
      link.click();
      toast.success(`Tarjeta (${side === 'front' ? 'frente' : 'reverso'}) descargada`);
    } catch (error) {
      console.error('Error downloading card:', error);
      toast.error('Error al descargar la tarjeta');
    } finally {
      setDownloading(false);
    }
  };

  const copyProfileUrl = () => {
    navigator.clipboard.writeText(profileUrl);
    setCopied(true);
    toast.success('Enlace copiado');
    setTimeout(() => setCopied(false), 2000);
  };

  const requestPrint = () => {
    const subject = encodeURIComponent(`Solicitud de Impresión de Tarjetas - ${user?.full_name}`);
    const body = encodeURIComponent(
      `Hola,\n\n` +
      `Me gustaría solicitar la impresión de 1,000 tarjetas de presentación.\n\n` +
      `--- DATOS DEL PEDIDO ---\n` +
      `Nombre: ${user?.full_name}\n` +
      `Email: ${user?.email}\n` +
      `Usuario: @${user?.username}\n` +
      `Plantilla seleccionada: ${currentTemplate.name}\n` +
      `Enlace de perfil: ${profileUrl}\n\n` +
      `Por favor, adjunto el diseño de mi tarjeta.\n\n` +
      `Gracias.`
    );
    window.location.href = `mailto:print@viubux.com?subject=${subject}&body=${body}`;
    toast.success('Abriendo cliente de correo...');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-2">
          <CreditCard className="w-5 h-5 text-violet-600" />
          Generador de Tarjetas de Presentación
        </h3>
        <p className="text-sm text-gray-600">
          Crea tarjetas profesionales con tu información y código QR. Descárgalas para imprimir o solicita nuestro servicio de impresión.
        </p>
      </div>

      {/* Template Selector */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
          <Palette className="w-4 h-4 text-violet-600" />
          Selecciona un Diseño
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {CARD_TEMPLATES.map((template) => (
            <button
              key={template.id}
              onClick={() => setSelectedTemplate(template.id)}
              className={`relative p-4 rounded-xl border-2 transition-all ${
                selectedTemplate === template.id
                  ? 'border-violet-500 ring-2 ring-violet-200'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className={`w-full h-12 rounded-lg ${template.bgColor} ${template.borderColor} border mb-2`} />
              <span className="text-sm font-medium text-gray-700">{template.name}</span>
              {selectedTemplate === template.id && (
                <div className="absolute top-2 right-2 w-5 h-5 bg-violet-500 rounded-full flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Custom Image Upload */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
          <Image className="w-4 h-4 text-violet-600" />
          Imagen/Logo Personalizado (Opcional)
        </h4>
        <p className="text-sm text-gray-500 mb-4">
          Sube tu logo o una imagen personalizada para usar en la tarjeta en lugar de tu foto de perfil.
        </p>
        
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleImageUpload}
          accept="image/*"
          className="hidden"
          id="custom-card-image-upload"
        />
        
        {customImagePreview ? (
          <div className="flex items-center gap-4">
            <img 
              src={customImagePreview} 
              alt="Imagen personalizada" 
              className="w-16 h-16 object-cover rounded-lg border border-gray-200"
            />
            <div className="flex-1">
              <p className="text-sm text-gray-700 font-medium">Imagen cargada</p>
              <p className="text-xs text-gray-500">Se usará esta imagen en la tarjeta</p>
            </div>
            <Button
              onClick={removeCustomImage}
              variant="outline"
              size="sm"
              className="text-red-500 border-red-200 hover:bg-red-50"
            >
              <X className="w-4 h-4 mr-1" />
              Quitar
            </Button>
          </div>
        ) : (
          <label 
            htmlFor="custom-card-image-upload"
            className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-violet-400 hover:bg-violet-50 transition-colors"
          >
            <Upload className="w-5 h-5 text-gray-400" />
            <span className="text-sm text-gray-600">Haz clic para subir una imagen o logo</span>
          </label>
        )}
      </div>

      {/* Card Preview */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h4 className="font-medium text-gray-900 mb-4">Vista Previa</h4>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Front of Card */}
          <div>
            <p className="text-sm text-gray-500 mb-2 text-center">Frente</p>
            <div
              ref={cardRef}
              className={`w-full aspect-[1.75/1] rounded-xl p-5 ${currentTemplate.bgColor} ${currentTemplate.borderColor} border shadow-lg flex flex-col justify-between overflow-hidden`}
              style={{ maxWidth: '400px', margin: '0 auto', fontSmooth: 'always', WebkitFontSmoothing: 'antialiased' }}
            >
              <div className="flex items-start gap-3">
                {customImagePreview ? (
                  <img
                    src={customImagePreview}
                    alt="Logo personalizado"
                    className="w-14 h-14 rounded-full object-cover border-2 border-white/30"
                    crossOrigin="anonymous"
                  />
                ) : user?.profile_photo ? (
                  <img
                    src={user.profile_photo}
                    alt={user.full_name}
                    className="w-14 h-14 rounded-full object-cover border-2 border-white/30"
                    crossOrigin="anonymous"
                  />
                ) : (
                  <div className={`w-14 h-14 rounded-full ${currentTemplate.accentColor} flex items-center justify-center`}>
                    <User className={`w-7 h-7 ${currentTemplate.id === 'gradient' ? 'text-violet-600' : 'text-white'}`} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className={`font-bold text-lg ${currentTemplate.textColor}`} style={{ wordBreak: 'break-word' }}>
                    {user?.full_name}
                  </h3>
                  <p className={`text-sm ${currentTemplate.textColor} opacity-70`}>
                    {user?.user_type === 'author' ? 'Autor' : 'Audio-Lector'} • ViubuxConnect
                  </p>
                </div>
              </div>
              
              <div className="flex items-end justify-between">
                <div className="space-y-1">
                  {user?.email && (
                    <p className={`text-xs ${currentTemplate.textColor} opacity-80`} style={{ wordBreak: 'break-all', maxWidth: '180px' }}>
                      {user.email}
                    </p>
                  )}
                  {profileData?.phone && (
                    <p className={`text-xs ${currentTemplate.textColor} opacity-80`}>
                      {profileData.phone}
                    </p>
                  )}
                </div>
                <div className="flex gap-1.5">
                  {profileData?.instagram && (
                    <div className={`w-6 h-6 rounded-full ${currentTemplate.accentColor} flex items-center justify-center`}>
                      <Instagram className={`w-3 h-3 ${currentTemplate.id === 'gradient' ? 'text-violet-600' : 'text-white'}`} />
                    </div>
                  )}
                  {profileData?.facebook && (
                    <div className={`w-6 h-6 rounded-full ${currentTemplate.accentColor} flex items-center justify-center`}>
                      <Facebook className={`w-3 h-3 ${currentTemplate.id === 'gradient' ? 'text-violet-600' : 'text-white'}`} />
                    </div>
                  )}
                  {profileData?.linkedin && (
                    <div className={`w-6 h-6 rounded-full ${currentTemplate.accentColor} flex items-center justify-center`}>
                      <Linkedin className={`w-3 h-3 ${currentTemplate.id === 'gradient' ? 'text-violet-600' : 'text-white'}`} />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-center mt-3">
              <Button
                onClick={() => downloadCard('front')}
                disabled={downloading}
                variant="outline"
                size="sm"
                className="text-violet-600 border-violet-200"
              >
                <Download className="w-4 h-4 mr-1" />
                Descargar Frente
              </Button>
            </div>
          </div>

          {/* Back of Card */}
          <div>
            <p className="text-sm text-gray-500 mb-2 text-center">Reverso</p>
            <div
              ref={cardBackRef}
              className={`w-full aspect-[1.75/1] rounded-xl p-5 ${currentTemplate.bgColor} ${currentTemplate.borderColor} border shadow-lg flex items-center justify-center`}
              style={{ maxWidth: '400px', margin: '0 auto' }}
            >
              <div className="text-center">
                <div className="bg-white p-3 rounded-xl inline-block mb-3 shadow-sm">
                  <QRCodeSVG
                    value={profileUrl}
                    size={80}
                    level="H"
                    includeMargin={false}
                    imageSettings={{
                      src: "/viubux_logo_web.png",
                      x: undefined,
                      y: undefined,
                      height: 16,
                      width: 16,
                      excavate: true,
                    }}
                  />
                </div>
                <p className={`text-sm font-medium ${currentTemplate.textColor}`}>
                  Escanea para conocer más
                </p>
                <p className={`text-xs ${currentTemplate.textColor} opacity-60 mt-1`}>
                  {profileUrl.replace('https://', '').replace('http://', '')}
                </p>
              </div>
            </div>
            <div className="flex justify-center mt-3">
              <Button
                onClick={() => downloadCard('back')}
                disabled={downloading}
                variant="outline"
                size="sm"
                className="text-violet-600 border-violet-200"
              >
                <Download className="w-4 h-4 mr-1" />
                Descargar Reverso
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Profile URL */}
      <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 min-w-0">
            <Globe className="w-5 h-5 text-violet-600 flex-shrink-0" />
            <code className="text-sm text-violet-700 truncate">{profileUrl}</code>
          </div>
          <Button
            onClick={copyProfileUrl}
            variant="outline"
            size="sm"
            className="text-violet-600 border-violet-300 flex-shrink-0"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Print Service CTA */}
      <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-6 text-white">
        <div className="flex flex-col md:flex-row items-center gap-4">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center shrink-0">
            <Printer className="w-8 h-8" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="text-xl font-bold mb-1">¿Quieres 1,000 tarjetas impresas?</h3>
            <p className="text-white/90 text-sm">
              Servicio de impresión profesional en papel premium con acabado mate o brillante. Entrega a domicilio en México.
            </p>
            <p className="text-2xl font-bold mt-2">$697 MXN</p>
          </div>
          <Button
            onClick={requestPrint}
            className="bg-white text-orange-600 hover:bg-orange-50 shrink-0 font-semibold"
          >
            <Mail className="w-4 h-4 mr-2" />
            Solicitar Impresión
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BusinessCardGenerator;
